import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import config from 'constants/config'
import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import AgentHubLoginStep from './AgentHubLoginStep'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { connect } from 'react-redux'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { checkUserExists, register } from 'actions/AuthActions'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useAgentHubAgentStatus from 'agentHub/hooks/useAgentHubAgentStatus'
import { useQsQueryParams } from 'hooks/useQueryParams'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import AgentHubAccountSetupStepIndicator from 'agentHub/components/AgentHubAccountSetupStepIndicator/AgentHubAccountSetupStepIndicator'

interface MappedStateProps {
  isLoggedIn?: boolean
  userEmail?: string
  agentHubAccount?: App.AgentHubAccount
}

interface Props {
  agentEmail: string
  onNextStep?: () => void
}

function AgentHubCreatePasswordSetupStep(props: MappedStateProps & Props) {
  const { isLoggedIn, agentEmail, onNextStep = noop } = props
  const { token } = useQsQueryParams<{ token: string }>()
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null)
  const reCaptchaRef = useRef<ReCAPTCHA>(null)

  const agent = useAgentHubAgentStatus({ registrationToken: token })

  useEffect(() => {
    dispatch(checkUserExists({ email: agentEmail }, reCaptchaRef.current))
  }, [agentEmail, dispatch])

  const emailExists = useAppSelector(state => state.auth.users[agentEmail]?.exists)
  const loading = useAppSelector(state => state.auth.processing)
  const hasAuthError = useAppSelector(state => state.auth.error)

  const passwordOnChange = useCallback(() => {
    passwordInputRef.current?.setCustomValidity('')
    confirmPasswordInputRef.current?.setCustomValidity('')
    formRef.current?.checkValidity()
  }, [])

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (passwordInputRef.current?.value !== confirmPasswordInputRef.current?.value) {
      const passwordsDoNotMatchError = intl.formatMessage({
        id: 'ZRH8FFCURJ2sJIdPN8_G',
        defaultMessage: 'Passwords do not match',
        description: 'Validation error when password and confirm password do not match in agent hub create password form',
      })
      passwordInputRef.current?.setCustomValidity(passwordsDoNotMatchError)
      confirmPasswordInputRef.current?.setCustomValidity(passwordsDoNotMatchError)
      formRef.current?.checkValidity()
      return
    }

    dispatch(register({
      email: agentEmail,
      password: passwordInputRef.current?.value ?? '',
      givenName: agent.data?.firstName ?? '',
      surname: agent.data?.surname ?? '',
      phone: agent.data?.phone ?? '',
      phonePrefix: agent.data?.phonePrefix ?? '',
    }, 'email'))
  }, [agent, agentEmail, dispatch, intl])

  useEffect(() => {
    if (isLoggedIn) {
      // logged in, don't need to setup password (or just did) -> go to next step
      onNextStep()
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  return (
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={20}>
          <AgentHubAccountSetupStepIndicator currentStep="login" />
          <div>
            <LoadingIndicator visible={loading} floating />
            {emailExists && <AgentHubLoginStep
              agentEmail={agentEmail}
              accountSetupCompleted={false}
            />}
            {!emailExists && (
              <Group direction="vertical" gap={24}>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'Zordt_iGEgpk4knhBR7-',
                    defaultMessage: 'Create your password for {agentEmail}',
                    description: 'Help text with dynamic email variable in sentence in the agent hub portal',
                  }, {
                    agentEmail,
                  })}
                </BodyText>
                <form ref={formRef} onSubmit={handleSubmit}>
                  <VerticalSpacer gap={16}>
                    <PasswordInput
                      autoComplete="new-password"
                      label={intl.formatMessage({
                        id: 'wdgT0WVEb1yLMnbhrzDT',
                        defaultMessage: 'Create password',
                        description: 'Create password input label in agent hub account setup',
                      })}
                      minLength={8}
                      name="user.password"
                      onChange={passwordOnChange}
                      ref={passwordInputRef}
                      required
                      requiredErrorMessage={intl.formatMessage({
                        id: 'YlMl02KlRJLCPYHJgOYE',
                        defaultMessage: 'Please enter a password',
                        description: 'Validation error when password field is empty in agent hub create password form',
                      })}
                    />

                    <PasswordInput
                      autoComplete="confirm-new-password"
                      label={intl.formatMessage({
                        id: 'oG0T9qHqU6v3Lo6Pmv8c',
                        defaultMessage: 'Confirm password',
                        description: 'Confirm password input label in agent hub account setup',
                      })}
                      minLength={8}
                      name="user.confirmPassword"
                      onChange={passwordOnChange}
                      ref={confirmPasswordInputRef}
                      required
                      requiredErrorMessage={intl.formatMessage({
                        id: '1PXNUM62OsJxc7oQxl_E',
                        defaultMessage: 'Please confirm your password',
                        description: 'Validation error when confirm password field is empty in agent hub create password form',
                      })}
                    />

                    {hasAuthError?.message && <MessageBanner kind="critical">{hasAuthError.message}</MessageBanner>}

                    <TextButton
                      type="submit"
                      kind="primary"
                      size="large"
                      fit="full-width"
                    >
                      {intl.formatMessage({
                        id: '6sylEK_1xZEZP0oX-yc7',
                        defaultMessage: 'Continue',
                        description: 'Continue button in agent hub create password form in the agent hub portal',
                      })}
                    </TextButton>
                  </VerticalSpacer>
                </form>
              </Group>
            )}
            {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reCaptchaRef} />}
          </div>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    isLoggedIn: selectLoggedIn(state),
    userEmail: state.auth.account.email,
    agentHubAccount: state.agentHub.agentHubAccount.account,
  }
})(AgentHubCreatePasswordSetupStep)
